/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import {
  VegaFlex,
  VegaFont,
  VegaBox,
  VegaInput,
  VegaForm,
  VegaTabGroupPanel,
  VegaPagination,
  VegaCheckbox,
  VegaButtonCircle
} from '@heartlandone/vega-react';
import MessageDetail from './MessageDetail';
import MessageItem from './MessageItem';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { initializeMessageState, showMessageDetail } from 'Slices/Header/messageSlice';
import { getMessages, updateMessages } from 'Chores/request';
import NotFoundMessage from '../../Public/Image/notFoundMessage.svg';
import NoMessage from '../../Public/Image/noMessage.svg';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const resolveFilterKey = (dataTarget) => {
  if (dataTarget === 'unread') return 'unread';
  if (dataTarget === 'system') return 'system';
  return 'all';
};

const decorateMessageList = (messages) =>
  messages.map((message) => {
    const read = typeof message?.isRead === 'boolean' ? message.isRead : true;
    return {
      ...message,
      read,
      selected: false,
      trigger: false,
      backgroundColor: read ? 'bg-primary' : 'bg-page'
    };
  });

const MessageTabGroupPanel = ({ item, userId, selectedTab, refreshCounts }) => {
  const dispatch = useDispatch();
  const showPagination = useSelector((state) => state.message.showPagination);
  const showMessageItemDetail = useSelector((state) => state.message.showMessageItemDetail);

  const sortType = [
    'far fa-arrow-down-short-wide',
    'far fa-arrow-up-short-wide'
  ];
  const [sortButtonType, setSortButtonType] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [localMessageList, setLocalMessageList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const previousSelectedTabRef = useRef(selectedTab);

  const isActive = item.dataTarget === selectedTab;
  const sortAscending = sortButtonType === 1;
  const filterKey = useMemo(() => resolveFilterKey(item.dataTarget), [item.dataTarget]);

  const fetchPage = useCallback(async () => {
    if (!userId) {
      setLocalMessageList([]);
      setTotalCount(0);
      return;
    }
    const params = {
      userId,
      filter: filterKey,
      skipCount: (pagination.page - 1) * pagination.pageSize,
      maxResultCount: pagination.pageSize,
      sorting: sortAscending ? 'creationTime asc' : 'creationTime desc',
      keyword: appliedSearch
    };
    try {
      const response = await getMessages(params);
      const items = response?.items || [];
      setTotalCount(response?.totalCount || 0);
      setLocalMessageList(decorateMessageList(items));
    } catch (error) {
      setLocalMessageList([]);
      setTotalCount(0);
    }
  }, [userId, filterKey, pagination.page, pagination.pageSize, sortAscending, appliedSearch]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    fetchPage();
  }, [isActive, fetchPage]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setAppliedSearch(searchTerm.trim());
      setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil((totalCount || 0) / pagination.pageSize));
    if (pagination.page > maxPage) {
      setPagination((prev) => ({ ...prev, page: maxPage }));
    }
  }, [totalCount, pagination.pageSize, pagination.page]);

  useEffect(() => {
    if (previousSelectedTabRef.current !== selectedTab) {
      if (item.dataTarget === selectedTab) {
        setPagination({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
        setSearchTerm('');
        setAppliedSearch('');
      }
      previousSelectedTabRef.current = selectedTab;
    }
  }, [selectedTab, item.dataTarget]);

  const allSelected = localMessageList.length > 0 && localMessageList.every((message) => message.selected);
  const selectedMessages = localMessageList.filter((message) => message.selected);
  const hasSelectedRead = selectedMessages.some((message) => message.isRead !== false);
  const hasSelectedUnread = selectedMessages.some((message) => message.isRead === false);

  const handleSearchChange = (e) => {
    const nextValue = typeof e?.detail === 'string' ? e.detail : '';
    setSearchTerm(nextValue);
  };

  const onChangeCheckBox = (e) => {
    const isChecked = e.target.checked;
    setLocalMessageList((prevList) =>
      prevList.map((message) => ({ ...message, selected: isChecked }))
    );
  };

  const changSortType = () => {
    setSortButtonType((prevType) => (prevType ? 0 : 1));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (e) => {
    const newPage = e.detail;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (e) => {
    const newPageSize = e.detail;
    setPagination({ page: 1, pageSize: newPageSize });
  };

  const batchDeleteMessages = async () => {
    const selectedMessages = localMessageList.filter((message) => message.selected);
    if (!selectedMessages.length) {
      return;
    }

    dispatch(initializeMessageState());

    const deletePayloads = selectedMessages
      .filter((message) => message?.id)
      .map((message) => ({ id: message.id, isDeleted: true, isRead: true }));

    if (deletePayloads.length) {
      try {
        await updateMessages(deletePayloads);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to batch delete messages', error);
      }
    }

    await fetchPage();
    await refreshCounts();
  };

  const batchMarkAsUnread = async () => {
    const selectedMessages = localMessageList.filter((message) => message.selected && message.isRead !== false);
    if (!selectedMessages.length) {
      return;
    }

    const unreadPayloads = selectedMessages
      .filter((message) => message?.id)
      .map((message) => ({ id: message.id, isRead: false }));

    if (unreadPayloads.length) {
      try {
        await updateMessages(unreadPayloads);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to batch mark messages as unread', error);
      }
    }

    await fetchPage();
    await refreshCounts();
  };

  const batchMarkAsRead = async () => {
    const selectedMessages = localMessageList.filter((message) => message.selected && message.isRead === false);
    if (!selectedMessages.length) {
      return;
    }

    const readPayloads = selectedMessages
      .filter((message) => message?.id)
      .map((message) => ({ id: message.id, isRead: true }));

    if (readPayloads.length) {
      try {
        await updateMessages(readPayloads);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to batch mark messages as read', error);
      }
    }

    await fetchPage();
    await refreshCounts();
  };

  const deleteMessage = async (messageItem) => {
    dispatch(initializeMessageState());

    if (messageItem?.id) {
      try {
        await updateMessages([{ id: messageItem.id, isDeleted: true }]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete message', error);
      }
    }

    await fetchPage();
    await refreshCounts();
  };
  const triggeredMessage = async (messageItem) => {
    const shouldUpdate = messageItem?.isRead === false;
    const detailPayload = { ...messageItem, ...(shouldUpdate ? { isRead: true } : {}) };
    dispatch(showMessageDetail(detailPayload));

    setLocalMessageList((prevList) =>
      prevList.map((message) => {
        if (message.id === messageItem.id) {
          return {
            ...message,
            isRead: shouldUpdate ? true : message.isRead,
            read: shouldUpdate ? true : message.read,
            trigger: true,
            backgroundColor: 'bg-table-selected-row'
          };
        }
        const readFlag = typeof message?.isRead === 'boolean' ? message.isRead : true;
        return {
          ...message,
          trigger: false,
          backgroundColor: readFlag ? 'bg-primary' : 'bg-page'
        };
      })
    );

    if (shouldUpdate) {
      try {
        await updateMessages([{ id: messageItem.id, isRead: true }]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update message read status', error);
      }
      await refreshCounts();
    }
  };
  const unTriggeredMessage = (messageItem) => {
    dispatch(initializeMessageState());
    setLocalMessageList((prevList) =>
      prevList.map((message) => {
        if (message.id === messageItem.id) {
          const readFlag = typeof message?.isRead === 'boolean' ? message.isRead : true;
          return {
            ...message,
            trigger: false,
            backgroundColor: readFlag ? 'bg-primary' : 'bg-page'
          };
        }
        return message;
      })
    );
  };

  return (
    <>
      <VegaTabGroupPanel id={item.dataTarget} dataVegaTabPanel={item.dataTarget}>
        <VegaFlex
          direction="row"
          alignItems="stretch"
          justifyContent="start"
          gap="0"
          flexGrow="initial"
        >
          <VegaBox padding="size-24" backgroundColor="bg-primary" flexGrow={1} flexBasis={0}>
            <VegaForm id="all-messages-form">
              <VegaFlex
                direction="col"
                alignItems="stretch"
                justifyContent="start"
                gap="size-16"
              >
                <VegaFlex
                  direction="col"
                  alignItems="stretch"
                  justifyContent="center"
                  gap="size-16"
                >
                  <VegaFont
                      color="text-sidebar-link-hover"
                      variant="font-h5"
                      style={{ display: 'block', verticalAlign: 'top', alignSelf: 'stretch' }}
                      textAlign="left"
                    >
                      {item.label}
                  </VegaFont>

                  <VegaInput
                    id="message-search-input"
                    dataVegaForm="input"
                    prefixIcon="far fa-search"
                    showClearIcon={false}
                    size='small'
                    value={searchTerm}
                    onVegaChange={handleSearchChange}
                  >
                  </VegaInput>
                </VegaFlex>

                <VegaFlex
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap="size-16"
                >
                  <VegaFlex
                    direction="row"
                    alignItems="center"
                    justifyContent="start"
                    gap="size-16"
                    style={{ marginLeft: '12px' }}
                  >
                    <VegaCheckbox
                        size="small"
                        disabled="false"
                        checked={allSelected}
                        onVegaChange={onChangeCheckBox}
                      >
                        Select All
                    </VegaCheckbox>
                    {selectedMessages.length > 0 ?
                      <VegaFlex
                        direction="row"
                        alignItems="center"
                        justifyContent="start"
                      >
                        {hasSelectedRead ?
                        <VegaButtonCircle
                          size="default"
                          disabled="false"
                          danger="false"
                          variant="icon-only"
                          icon="far fa-envelope"
                          iconColor="text-sidebar-link-hover"
                          id="Batch-mark-as-unread-message-button"
                          onVegaClick={batchMarkAsUnread}
                        >
                        </VegaButtonCircle> :
                        ''}
                        {hasSelectedUnread ?
                        <VegaButtonCircle
                          size="default"
                          disabled="false"
                          danger="false"
                          variant="icon-only"
                          icon="far fa-envelope-open"
                          iconColor="text-sidebar-link-hover"
                          id="Batch-mark-as-read-message-button"
                          onVegaClick={batchMarkAsRead}
                        >
                        </VegaButtonCircle> :
                        ''}
                        <VegaButtonCircle
                          size="default"
                          disabled="false"
                          danger="false"
                          variant="icon-only"
                          icon="far fa-trash-can"
                          iconColor="text-sidebar-link-hover"
                          id="Batch-delete-message-button"
                          onVegaClick={batchDeleteMessages}
                        >
                        </VegaButtonCircle>
                      </VegaFlex> :
                    ''}
                  </VegaFlex>

                  <VegaButtonCircle
                      size="default"
                      disabled="false"
                      danger="false"
                      variant="icon-only"
                      icon={sortType[sortButtonType ? 1 : 0]}
                      onVegaClick={changSortType}
                      iconColor="text-sidebar-link-hover"
                    >
                  </VegaButtonCircle>
                </VegaFlex>

                {localMessageList.length > 0 ? localMessageList.map((messageItem) =>
                  <MessageItem
                    key={messageItem.id}
                    messageItem={messageItem}
                    setLocalMessageList={setLocalMessageList}
                    triggeredMessage={triggeredMessage}
                    unTriggeredMessage={unTriggeredMessage}
                  />):
                <VegaFlex
                  dataFigmaNodeName='Empty Page'
                  direction='col'
                  alignItems='center'
                  justifyContent='center'
                  gap='0'
                >
                  <img
                    style={{ height: '200px', width: '200px' }}
                    src={searchTerm ? NotFoundMessage : NoMessage}
                  />

                  <VegaBox
                    dataFigmaNodeName='Title'
                    padding={{ top: 'size-32', right: '0', bottom: '0', left: '0' }}
                    corners='rounded-none'
                  >
                    <VegaFont
                      dataFigmaNodeName='Title'
                      color='text-primary'
                      variant='font-h4'
                      style={{
                        display: 'block',
                        verticalAlign: 'top',
                        alignSelf: 'stretch'
                      }}
                      textAlign='center'
                    >
                      {searchTerm ? 'No Results Found' : 'No Messages Found'}
                    </VegaFont>
                  </VegaBox>
                </VegaFlex>}
            {
              showPagination && totalCount > 0 ?
              <VegaPagination
                current={pagination.page}
                page-size={pagination.pageSize}
                total={totalCount}
                extensions={[
                  ['currentPageLabel', 'pageSizeSelector'],
                  ['pageSelector', 'goTo']
                ]}
                onVegaChange={handlePageChange}
                onVegaPageSizeChange={handlePageSizeChange}
              >
              </VegaPagination> : ''}
              </VegaFlex>
            </VegaForm>
          </VegaBox>
          {
            showMessageItemDetail ?
            <MessageDetail
              unTriggeredMessage={unTriggeredMessage}
              deleteMessage={deleteMessage}
            /> : ''}

        </VegaFlex>
      </VegaTabGroupPanel>
    </>
  );
};

MessageTabGroupPanel.propTypes = {
  item: PropTypes.object,
  userId: PropTypes.string,
  selectedTab: PropTypes.string,
  refreshCounts: PropTypes.func
};

export default MessageTabGroupPanel;
